import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { releasesService } from './service';
import type { Release, ReleaseListQuery, UpdateReleaseRequest } from './types';

/** Local to the feature, like its endpoints. */
export const releaseKeys = {
  all: ['release-management'] as const,
  list: (query: ReleaseListQuery) => [...releaseKeys.all, 'list', query] as const,
  detail: (id: string) => [...releaseKeys.all, 'detail', id] as const,
  announcement: (id: string) => [...releaseKeys.all, 'announcement', id] as const,
};

export function useReleases(query: ReleaseListQuery) {
  return useQuery({
    queryKey: releaseKeys.list(query),
    queryFn: ({ signal }) => releasesService.list(query, signal),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useRelease(id: string) {
  return useQuery({
    queryKey: releaseKeys.detail(id),
    queryFn: ({ signal }) => releasesService.get(id, signal),
  });
}

export function useAnnouncement(id: string, enabled: boolean) {
  return useQuery({
    queryKey: releaseKeys.announcement(id),
    queryFn: ({ signal }) => releasesService.announcement(id, signal),
    enabled,
  });
}

/** Every write returns the release; the lists refetch around it. */
function useSettle() {
  const queryClient = useQueryClient();
  return (release: Release) => {
    queryClient.setQueryData(releaseKeys.detail(release.id), release);
    void queryClient.invalidateQueries({ queryKey: [...releaseKeys.all, 'list'] });
    void queryClient.invalidateQueries({ queryKey: releaseKeys.announcement(release.id) });
  };
}

export function useUpdateRelease(id: string) {
  const settle = useSettle();
  return useMutation({
    mutationFn: (body: UpdateReleaseRequest) => releasesService.update(id, body),
    onSuccess: (release) => {
      settle(release);
      toast.success('Release saved.');
    },
  });
}

export function useReleaseNow(id: string) {
  const settle = useSettle();
  return useMutation({
    mutationFn: () => releasesService.release(id),
    onSuccess: (release) => {
      settle(release);
      toast.success(
        `${release.versionNumber} released. Desktops on ${release.releaseChannel} get it from their next update check.`,
      );
    },
  });
}

export function useWithdrawRelease(id: string) {
  const settle = useSettle();
  return useMutation({
    mutationFn: () => releasesService.withdraw(id),
    onSuccess: (release) => {
      settle(release);
      toast.success(`${release.versionNumber} withdrawn. It is no longer offered.`);
    },
  });
}

export function useAnnounceRelease(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => releasesService.announce(id),
    onSuccess: async ({ sent, failed }) => {
      await queryClient.invalidateQueries({ queryKey: releaseKeys.announcement(id) });
      toast.success(
        `Release email queued for ${String(sent)} ${sent === 1 ? 'person' : 'people'}` +
          (failed ? `; ${String(failed)} could not be queued.` : '.'),
      );
    },
  });
}
